import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider, useT } from "./lib/i18n";
import { api, formatApiError } from "./lib/api";
import { isCompanyRegistered } from "./lib/company";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import CompanyPage from "./pages/CompanyPage";
import NewProject from "./pages/NewProject";
import ProjectDetail from "./pages/ProjectDetail";
import DeviceDetail from "./pages/DeviceDetail";
import DemoClassification from "./pages/DemoClassification";
import BillingPage from "./pages/BillingPage";
import { Toaster } from "./components/ui/sonner";
import WorkspaceLoading from "./components/WorkspaceLoading";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <WorkspaceLoading fullScreen />;
  if (!user) {
    const from = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
    return <Navigate to="/login" replace state={{ from }} />;
  }
  return children;
}

function CompanyRequirement({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const t = useT();
  const [retry, setRetry] = useState(0);
  const [check, setCheck] = useState({ userId: null, loading: true, complete: false, error: "" });

  useEffect(() => {
    let active = true;
    setCheck({ userId: user?.id, loading: true, complete: false, error: "" });
    api.get("/user/profile")
      .then(({ data }) => {
        if (active) setCheck({ userId: user?.id, loading: false, complete: isCompanyRegistered(data), error: "" });
      })
      .catch((error) => {
        if (active) setCheck({
          userId: user?.id,
          loading: false,
          complete: false,
          error: formatApiError(error.response?.data?.detail),
        });
      });
    return () => { active = false; };
  }, [user?.id, retry]);

  if (check.loading || check.userId !== user?.id) {
    return <WorkspaceLoading fullScreen />;
  }

  if (check.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="max-w-lg text-sm text-red-700">{check.error}</p>
        <button onClick={() => setRetry((current) => current + 1)} className="btn-primary px-4 py-2 text-sm">
          {t("app.retry")}
        </button>
      </div>
    );
  }

  if (!check.complete) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/company?setup=1" state={{ from }} replace />;
  }

  return children;
}

function Workspace({ children }) {
  const { user } = useAuth();
  const invitedViewer = user?.workspace_role === "viewer" && user?.workspace_id !== user?.id;
  if (user?.account_tier === "demo" && user?.role !== "admin") {
    if (invitedViewer) return <Protected>{children}</Protected>;
    return <Navigate to="/demo/classification" replace />;
  }
  return <Protected><CompanyRequirement>{children}</CompanyRequirement></Protected>;
}

function CompanyWorkspace() {
  const { user } = useAuth();
  if (user?.account_tier === "demo" && user?.role !== "admin"
    && !(user?.workspace_role === "viewer" && user?.workspace_id !== user?.id)) {
    return <Navigate to="/demo/classification" replace />;
  }
  return <Protected><CompanyPage /></Protected>;
}

export default function App() {
  useEffect(() => {
    const setupLemonSqueezy = () => {
      if (window.LemonSqueezy && !window.__mdrflowLemonSqueezySetup) {
        window.LemonSqueezy.Setup();
        window.__mdrflowLemonSqueezySetup = true;
      }
    };
    const lemonScript = document.querySelector('script[src="https://app.lemonsqueezy.com/js/lemon.js"]');
    if (window.LemonSqueezy) {
      setupLemonSqueezy();
      return undefined;
    }
    lemonScript?.addEventListener("load", setupLemonSqueezy);
    return () => lemonScript?.removeEventListener("load", setupLemonSqueezy);
  }, []);

  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/register-demo" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/company" element={<CompanyWorkspace />} />
            <Route path="/demo/classification" element={<Protected><DemoClassification /></Protected>} />
            <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
            <Route path="/dashboard" element={<Workspace><Dashboard /></Workspace>} />
            <Route path="/projects/new" element={<Workspace><NewProject /></Workspace>} />
            <Route path="/projects/:pid/devices/new" element={<Workspace><DeviceDetail createNew /></Workspace>} />
            <Route path="/projects/:pid" element={<Workspace><ProjectDetail /></Workspace>} />
            <Route path="/devices/new" element={<Protected><Navigate to="/dashboard" replace /></Protected>} />
            <Route path="/devices/:did" element={<Workspace><DeviceDetail /></Workspace>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </I18nProvider>
  );
}
